// USD estimates: cents rounded at the monthly total, no fees counted twice.
export function computeTotals({ monthlyReceipts, monthlyTransactions, splashMonthly = 79.99, competitorMonthly = 0, competitorPct = 0, processingPct = 2.9, processingPerTxn = 0.30 }) {
    const nonNegative = (v) => Number.isFinite(Number(v)) ? Math.max(0, Number(v)) : 0;
    const receipts = nonNegative(monthlyReceipts);
    const transactions = Math.floor(nonNegative(monthlyTransactions));
    const round = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
    const splashProcessing = round(receipts * 0.029 + transactions * 0.30);
    const splashPlatform = round(transactions * 0.25);
    const competitorProcessing = round(receipts * nonNegative(processingPct) / 100 + transactions * nonNegative(processingPerTxn));
    const competitorPlatform = round(receipts * nonNegative(competitorPct) / 100);
    const splashTotalMonthly = round(nonNegative(splashMonthly) + splashProcessing + splashPlatform);
    const competitorTotalMonthly = round(nonNegative(competitorMonthly) + competitorProcessing + competitorPlatform);
    const differenceMonthly = round(competitorTotalMonthly - splashTotalMonthly);
    return { splashProcessing, splashPlatform, competitorProcessing, competitorPlatform, splashTotalMonthly, competitorTotalMonthly, differenceMonthly,
        outcome: differenceMonthly > 0 ? 'cheaper' : differenceMonthly < 0 ? 'more expensive' : 'tied',
        splashTotal: round(splashTotalMonthly * 12), competitorTotal: round(competitorTotalMonthly * 12), annualGross: receipts * 12, annualTxns: transactions * 12 };
}


/**
 * competitorFeeData.js
 *
 * Verified public-pricing snapshots for the swim-lesson / class-registration
 * software landscape as of 2026-Q1. Powers the dropdown in the
 * <LandingProfitCalculator />.
 *
 * Editorial rules:
 *   • Never invent numbers. Where a vendor is quote-based, seed a
 *     conservative "typical mid-tier" default and mark it "Estimate"
 *     in the `source` string.
 *   • Every entry MUST be overridable by the user. The calculator UI
 *     exposes editable Monthly + Platform-fee % inputs so a coach
 *     can enter their real bill.
 *   • Keep the tone neutral. The dropdown lets numbers speak. This is
 *     NOT a competitor hit-piece.
 *   • Include "Custom / Other" so any coach can plug in whatever they
 *     use — and (optionally) tell us the name of it so we know which
 *     platforms coaches are actually migrating from.
 *
 * Fee model semantics used everywhere in the calculator:
 *   monthlyFlat   — fixed $/mo billed by the vendor (subscription)
 *   platformPct   — percentage of GROSS revenue skimmed by the vendor
 *                    on top of the subscription (0 for most flat-price
 *                    platforms; 1–23% for revenue-share platforms).
 *
 * Swum pricing was rechecked on 2026-10-06; other vendor snapshots retain
 * their individual source dates/estimate labels and should be overridden
 * with the coach's actual bill.
 * platformPct excludes processing. processingPct + processingPerTxn are
 * explicit for Swum (Standard/Pro total fees already include their 3%).
 * Other vendors use an illustrative 2.9% + $0.30 assumption, NOT a claim
 * that every provider charges Stripe's rate.
 */

export const COMPETITOR_FEES = [
    // ------------- Purpose-built swim / revenue-share -------------
    {
        id: 'swum-ai-standard',
        label: 'Swum.ai — Standard',
        monthlyFlat: 0,
        platformPct: 20,
        processingPct: 3,
        processingPerTxn: 0,
        source: 'https://swum.ai/pricing/',
        verifiedAt: '2026-10-06',
    },
    {
        id: 'swum-ai-pro',
        label: 'Swum.ai — Pro',
        monthlyFlat: 98,
        platformPct: 2,
        processingPct: 3,
        processingPerTxn: 0,
        source: 'https://swum.ai/pricing/',
        verifiedAt: '2026-10-06',
    },
    {
        id: 'swum-ai-pro-max',
        label: 'Swum.ai — Pro Max',
        monthlyFlat: 200,
        platformPct: 0,
        processingPct: 2.9,
        processingPerTxn: 0.30,
        source: 'https://swum.ai/pricing/',
        verifiedAt: '2026-10-06',
    },

    // ------------- Enterprise swim / class management ------------
    {
        id: 'jackrabbit-small',
        label: 'Jackrabbit Swim (0–100 students)',
        monthlyFlat: 49,
        platformPct: 0,
        source: 'jackrabbitclass.com/pricing, 2026-Q1 (starter tier)',
    },
    {
        id: 'jackrabbit-mid',
        label: 'Jackrabbit Swim (101–500 students)',
        monthlyFlat: 129,
        platformPct: 0,
        source: 'jackrabbitclass.com/pricing, 2026-Q1 (mid tier)',
    },
    {
        id: 'iclasspro',
        label: 'iClassPro (Signature, per location)',
        monthlyFlat: 129,
        platformPct: 0,
        source: 'iclasspro.com/pricing, 2026-Q1',
    },
    {
        id: 'mindbody',
        label: 'Mindbody (Essential)',
        monthlyFlat: 159,
        platformPct: 0,
        source: 'mindbody.io/pricing, 2026-Q1 estimate',
    },
    {
        id: 'swimtopia',
        label: 'SwimTopia (typical mid-tier)',
        monthlyFlat: 149,
        platformPct: 0,
        source: 'Estimate — quote-based, scales with athlete count. Please override with your bill.',
    },
    {
        id: 'teamunify',
        label: 'TeamUnify (typical mid-tier)',
        monthlyFlat: 129,
        platformPct: 0,
        source: 'Estimate — quote-based, scales with athlete count. Please override.',
    },
    {
        id: 'classcard',
        label: 'Classcard',
        monthlyFlat: 100,
        platformPct: 0,
        source: 'classcardapp.com/pricing, 2026-Q1 (flat fee)',
    },

    // ------------- General class / kids-activities registration --
    {
        id: 'amilia',
        label: 'Amilia SmartRec (Essential)',
        monthlyFlat: 99,
        platformPct: 1,
        source: 'amilia.com/pricing, 2026-Q1 (includes 1% service fee)',
    },
    {
        id: 'proclass-standard',
        label: 'ProClass (Standard)',
        monthlyFlat: 45,
        platformPct: 0,
        source: 'proclasswebware.com/pricing, 2026-Q1',
    },
    {
        id: 'proclass-premium',
        label: 'ProClass (Premium)',
        monthlyFlat: 105,
        platformPct: 0,
        source: 'proclasswebware.com/pricing, 2026-Q1',
    },
    {
        id: 'pembee',
        label: 'Pembee',
        monthlyFlat: 45,
        platformPct: 0,
        source: 'pembee.app/pricing, 2026-Q1',
    },
    {
        id: 'regpack',
        label: 'Regpack (SMB, annual)',
        monthlyFlat: 24,
        platformPct: 0,
        source: 'regpacks.com/pricing, 2026-Q1 (1-year contract)',
    },
    {
        id: 'sawyer-launch',
        label: 'Sawyer — Launch',
        monthlyFlat: 0,
        platformPct: 3,
        source: 'hisawyer.com/for-business/pricing, 2026-Q1',
    },
    {
        id: 'sawyer-grow',
        label: 'Sawyer — Grow',
        monthlyFlat: 189,
        platformPct: 0,
        source: 'hisawyer.com/for-business/pricing, 2026-Q1 (annual)',
    },
    {
        id: 'coursestorm',
        label: 'CourseStorm',
        monthlyFlat: 0,
        platformPct: 5,
        source: 'coursestorm.com/pricing, 2026-Q1 (~4–5% per registration)',
    },

    // ------------- General booking calendars ---------------------
    {
        id: 'acuity',
        label: 'Acuity Scheduling (Growing)',
        monthlyFlat: 34,
        platformPct: 0,
        source: 'squareup.com/appointments/plans, 2026-Q1',
    },

    // ------------- User-entered fallback -------------------------
    {
        id: 'custom',
        label: 'Custom / Other — enter your own numbers',
        monthlyFlat: 0,
        platformPct: 0,
        source: null,
    },
];

export function getCompetitorFee(id) {
    return COMPETITOR_FEES.find((c) => c.id === id) || COMPETITOR_FEES[0];
}

/**
 * Map a comparisons.js slug to the recommended default option in the
 * dropdown, so when the calculator is embedded on a specific /vs/*
 * page the dropdown pre-selects the right competitor.
 */
export const SLUG_TO_COMPETITOR_ID = {
    'swum-ai': 'swum-ai-pro',
    'jackrabbit-swim': 'jackrabbit-mid',
    'iclasspro': 'iclasspro',
    'mindbody': 'mindbody',
    'swimtopia': 'swimtopia',
    'teamunify': 'teamunify',
    'acuity-scheduling': 'acuity', // legacy slug (pre-Sept-2026 audit fix)
    'acuity': 'acuity',
};
